import React, { createContext, useContext, useState } from "react";
import { Project } from "../../interfaces/Project";
import { Estimate } from "../../interfaces/Estimate";
import { GCGR } from "../../interfaces/GCGRs";
import { CostModel } from "../../interfaces/CostModel";

interface ProjectContextType {
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  selectedEstimate: Estimate | null;
  setSelectedEstimate: (estimate: Estimate | null) => void;
  selectedGcgr: GCGR | null;
  setSelectedGcgr: (gcgr: GCGR | null) => void;
  /**
   * Whether the workbook holds contracting data. Contracting has no entity of its own, and it is
   * only ever loaded for the selected project, so presence needs no project of its own to compare.
   */
  isContractingLoaded: boolean;
  setIsContractingLoaded: (loaded: boolean) => void;
  selectedCostModel: CostModel | null;
  setSelectedCostModel: (costModel: CostModel | null) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedProject, setSelectedProjectState] = useState<Project | null>(null);
  const [selectedEstimate, setSelectedEstimateState] = useState<Estimate | null>(null);
  const [selectedGcgr, setSelectedGcgr] = useState<GCGR | null>(null);
  const [isContractingLoaded, setIsContractingLoaded] = useState<boolean>(false);
  const [selectedCostModel, setSelectedCostModel] = useState<CostModel | null>(null);

  const setSelectedProject = (project: Project | null) => {
    // Check if project is changing to a different project (both must be non-null)
    // This prevents clearing estimate when restoring from null during cache restoration
    const isProjectChanging = selectedProject !== null && project !== null && project.id !== selectedProject.id;

    setSelectedProjectState(project);
    if (project?.id) {
      // Clear selectedEstimate only when changing from one project to a different project
      if (isProjectChanging) {
        setSelectedEstimateState(null);
        setSelectedGcgr(null);
        setIsContractingLoaded(false);
        setSelectedCostModel(null);
      }
    } else {
      // Clear selectedEstimate when project is cleared
      setSelectedEstimateState(null);
      setSelectedGcgr(null);
      setIsContractingLoaded(false);
      setSelectedCostModel(null);
    }
  };

  return (
    <ProjectContext.Provider
      value={{
        selectedProject,
        setSelectedProject,
        selectedEstimate,
        setSelectedEstimate: setSelectedEstimateState,
        selectedGcgr,
        setSelectedGcgr,
        isContractingLoaded,
        setIsContractingLoaded,
        selectedCostModel,
        setSelectedCostModel,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProject must be used within a ProjectProvider");
  }
  return context;
};
